import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Card, Spin, Tooltip, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import AiMappingRow from "components/molecules/AiMappingRow";
import React from "react";

const { Title, Text } = Typography;

interface AiMapping {
    sourceColumn: string;
    targetAttribute: string;
    confidence: number;
    reasoning: string;
    transformations: any[];
    sourceColumnExists: boolean;
    targetAttributeExists: boolean;
}

interface AiMappingSuggestionsProps {
    mappings: AiMapping[];
    summary?: {
        totalMappings: number;
        highConfidenceMappings: number;
        mediumConfidenceMappings: number;
        lowConfidenceMappings: number;
    };
    loading?: boolean;
    onRefresh?: () => void;
    onMappingChange?: (sourceColumn: string, newTarget: string) => void;
    targetOptions?: Array<{ label: string; value: string }>;
    className?: string;
}

const AiMappingSuggestions: React.FC<AiMappingSuggestionsProps> = ({ mappings = [], summary, loading = false, onRefresh, onMappingChange, targetOptions = [], className = "" }) => {
    const handleMappingChange = (sourceColumn: string, newTarget: string) => {
        if (onMappingChange) {
            onMappingChange(sourceColumn, newTarget);
        }
    };

    const getConfidenceSummary = () => {
        if (!summary) return null;

        return (
            <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Total: {summary.totalMappings}</span>
                <span className="text-green-600">High: {summary.highConfidenceMappings}</span>
                <span className="text-yellow-600">Medium: {summary.mediumConfidenceMappings}</span>
                <span className="text-red-600">Low: {summary.lowConfidenceMappings}</span>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Spin size="large" />
            </div>
        );
    }

    if (!mappings || mappings.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 text-gray-500">
                <Text>No AI mapping suggestions available</Text>
            </div>
        );
    }

    return (
        <div className={mergeClassNames("ai-mapping-suggestions", className)}>
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Title level={4} className="mb-0">
                        AI Mapping Suggestions
                    </Title>
                    <Tooltip title="AI-generated field mapping suggestions based on your data and schema">
                        <InfoCircleOutlined className="cursor-help text-lg text-gray-400" />
                    </Tooltip>
                </div>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh} size="small">
                        Refresh Suggestions
                    </Button>
                )}
            </div>

            {/* Summary Stats */}
            {getConfidenceSummary()}

            {/* Mapping Table */}
            <Card className="mt-4">
                {/* Table Headers */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-200 bg-gray-50 p-4 font-medium text-gray-700">
                    <div className="flex items-center gap-2">
                        <Text strong>Source Fields (from your file)</Text>
                        <Tooltip title="Fields available in your source data">
                            <InfoCircleOutlined className="text-sm text-gray-400" />
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-2">
                        <Text strong>Mapped Target Fields (AI Suggestions)</Text>
                        <Tooltip title="AI-suggested mappings to target schema fields">
                            <InfoCircleOutlined className="text-sm text-gray-400" />
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-2">
                        <Text strong>Mapping Details</Text>
                        <Tooltip title="Final mapping information and reasoning">
                            <InfoCircleOutlined className="text-sm text-gray-400" />
                        </Tooltip>
                    </div>
                </div>

                {/* Mapping Rows */}
                <div className="divide-y divide-gray-100">
                    {mappings.map((mapping, index) => (
                        <AiMappingRow
                            key={`${mapping.sourceColumn}-${index}`}
                            sourceColumn={mapping.sourceColumn}
                            targetAttribute={mapping.targetAttribute}
                            confidence={mapping.confidence}
                            reasoning={mapping.reasoning}
                            sourceColumnExists={mapping.sourceColumnExists}
                            targetAttributeExists={mapping.targetAttributeExists}
                            onTargetChange={(newTarget) => handleMappingChange(mapping.sourceColumn, newTarget)}
                            targetOptions={targetOptions}
                        />
                    ))}
                </div>
            </Card>
        </div>
    );
};

export default AiMappingSuggestions;
